import { useLingui } from '@lingui/react/macro'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { RowItem } from 'components/RowItemPicker'
import { PermissionsGate } from 'features/permissions'
import React from 'react'
import styled from 'styled-components'
import { ThemeMode, useGeckoTheme } from 'styles/GeckoTheme'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

const StyledContainer = styled.div`
  padding: 0.25rem 0.25rem 0.75rem 1rem;
  margin-bottom: -0.5rem;
  width: 100%;
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  align-items: center;
  overflow-x: scroll;
`

/**
 * Built on DropdownMenu.RadioGroup so each theme option becomes a real menu
 * item that participates in the dropdown's arrow-key roving focus (Radix menus
 * disable Tab, so loose buttons would be unreachable by keyboard).
 */
const ThemeRadioGroup = styled(DropdownMenu.RadioGroup)`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0.25rem;
  height: 32px;
  gap: 0.25rem;
  width: fit-content;
  background-color: ${({ theme }) => theme.forecast.background3};
  border-radius: 0.25rem;
`

export const ThemeModeToggle: React.FC = () => {
  const { themeMode, setThemeMode } = useGeckoTheme()
  const { t } = useLingui()
  const labelId = React.useId()

  const basicThemes = [ThemeMode.Dark, ThemeMode.Light, ThemeMode.Zinc]
  const internalThemes = [
    ThemeMode.Freedom,
    ThemeMode.Spooky,
    ThemeMode.HotDog,
    ThemeMode.Barbie,
    ThemeMode.Brat,
    ThemeMode.BratRemix,
  ]

  const handleThemeModeChange = (theme: ThemeMode): void => {
    setThemeMode(theme)
  }

  return (
    <StyledContainer>
      <span id={labelId}>{t`Theme`}</span>
      <ThemeRadioGroup
        role='group'
        aria-labelledby={labelId}
        value={themeMode}
        onValueChange={(value) => handleThemeModeChange(value as ThemeMode)}>
        {basicThemes.map((theme) => (
          <DropdownMenu.RadioItem
            key={theme}
            asChild
            value={theme}
            onSelect={(event) => event.preventDefault()}>
            <RowItem
              $isActive={theme === themeMode}
              aria-label={t`Change theme to ${theme}`}>
              {theme}
            </RowItem>
          </DropdownMenu.RadioItem>
        ))}
        <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
          {internalThemes.map((theme) => (
            <DropdownMenu.RadioItem
              key={theme}
              asChild
              value={theme}
              onSelect={(event) => event.preventDefault()}>
              <RowItem
                $isActive={theme === themeMode}
                aria-label={t`Change theme to ${theme}`}>
                {theme}
              </RowItem>
            </DropdownMenu.RadioItem>
          ))}
        </PermissionsGate>
      </ThemeRadioGroup>
    </StyledContainer>
  )
}
